import { create } from 'zustand';

import cookieInfo from '@alife/bc-cookie-info';

import { getTimeZone } from '@/common/getTimeZone';
import doLog from '@/common/log';
import urlParam from '@/common/url-param';

const BASE_URL = 'https://www.alibaba.com/event/app/productDetail/logistics.do';

type Express = {
  express: Partial<ExpressTypes.ExpressInfo>;
  arg?: PropType;
  isCall: boolean;
  selected: string;
  logisticPrice: number;
  isEmpty: boolean;
};

type State = {
  [k in SkuTypes.TypeInfo]: Express;
} & {
  dispatchId: string; //发货地，
};

interface PropType {
  type: SkuTypes.TypeInfo;
  [t: string]: string | number | object;
}

type Action = {
  fetch: (params: PropType, quantity: number) => void;
  setDispatchById: (params: string) => void;
  setReset: (type: SkuTypes.TypeInfo) => void;
  upExpress: (payload: {
    type: SkuTypes.TypeInfo;
    // 当前选择的物流线路
    currentMethod?: string;
    quantity?: number;
    selected?: string;
    express: Partial<ExpressTypes.ExpressInfo>;
  }) => void;
};

/**{
  "productId": "1600951190373", 
  "packageMode": "piece", //piece 或者 box
  "orderSource": "buyNow", //样品单传 buySample
  "timeZone": "Asia/Shanghai",
  "language": "zh-Hans", 
  "shipToCountry": "US", // shipto地址（收货地） 如果为空，则从session中取，默认US
  "currencyCode": "USD", // 可以不填，从session中取，默认美元
  "shipToZipCode": "35004", // shipto邮编，非必填,前端cookie找
  "quantity": 4, // sku总数量
  "dispatchCountry": "CN", // 发货仓库
  "boxMoq": 12, //按箱moq //detailData.product.boxMoq
  "currentMethod": "SEMI_MANAGED_ECONOMIC@@ECONOMY", // 透穿运力线
  "skuQuantity":"105075543323:1,105075543325:3,",
  "extraParams": "{mode: normal}", //  扩展字段，暂时PC不用传
  "cna":"F2I2Hrunm3YCASp4SS4Yy Jm"
}*/
//type是调用fetch函数传入的
const globalParam = (type: string, params: PropType) => {
  return {
    /* 待传入的字段
     * "productId": "1600951190373", 
      "quantity": 4, // sku总数量
      "boxMoq": 12, //按箱moq //detailData.product.boxMoq
      "currentMethod": "SEMI_MANAGED_ECONOMIC@@ECONOMY", // 透穿运力线
      "skuQuantity":"105075543323:1,105075543325:3,",
      "extraParams": "{mode: normal}", // 扩展字段，暂时PC不用传
     */
    packageMode: 'piece', //默认按件售卖
    orderSource: type === 'sample' ? 'buySample' : 'buyNow', //默认请求主商品物流，若为样品物流则传入‘buySample’
    timeZone: getTimeZone(cookieInfo.getBuyerLocale()),
    language: cookieInfo.getBuyerLocale(),
    shipToCountry: 'US', //收货地
    currencyCode: cookieInfo.getBuyerCurrency() || 'USD',
    shipToZipCode: cookieInfo.getZipCode(),
    dispatchCountry: 'US', //发货地
    cna: cookieInfo.getCNA(),
    ...params,
  };
};

const defaultObj = {
  isEmpty: true,
  isCall: false,
  selected: '',
  logisticPrice: 0,
  express: {
    price: 0,
  },
};

let latestRequestId = 0;

const ExpressStore = create<State & Action>(set => ({
  product: defaultObj,
  sample: defaultObj,
  inquiry: defaultObj,
  dispatchId: '', //发货地
  setReset: type => {
    set(state => ({
      [type]: defaultObj,
    }));
  },
  fetch: async (params, quantity) => {
    const { type } = params;
    // 生成唯一的请求标识符
    const requestId = ++latestRequestId;
    const expressUrl = `${BASE_URL}?requestJson=${encodeURIComponent(JSON.stringify(globalParam(type, params)))}`;

    const response = await fetch(expressUrl).then(res => res.json());
    if (requestId !== latestRequestId) return;
    if (response.data.logisticGroup?.length > 0) {
      doLog('/sc.details.logisticsShow', `requestid=${response.data.requestId}`);
      const {
        data,
      }: {
        data: ExpressTypes.RootObject;
      } = response;
      /**
       * getExpress():对物流进行过滤，选出正在显示的物流线路相关数据
       * @param selected 选中的物流线路code字符串
       * @param logisticGroup response.data.logisticGroup
       * @returns 结构如{ innerFloor:{} , outerFloor:{} , skuModel:{} }
       */
      const getExpress = (selected: string, logisticGroupList: ExpressTypes.ExpressList[]) => {
        // 如果有已经选中的物流线路 selected是物流线路code字符串
        if (selected) {
          const expressInfo = logisticGroupList.filter(
            i => i.innerFloor.data.methodCode === selected
          );
          if (expressInfo.length > 0) {
            return expressInfo[0];
          }
        }
        // 获取物流默认运力线
        const defaultExpress = logisticGroupList.filter(i => {
          return i.innerFloor.data.isSelected === true;
        });

        if (defaultExpress.length === 1) {
          return defaultExpress[0];
        }
        // 兜底默认选logisticGroup中的第一个物流线路
        return logisticGroupList[0];
      };

      // 物流过滤
      set(state => ({
        ...state,
        [type]: (() => {
          const expressData = getExpress(
            // 物流穿透选择的运力线
            state[type].selected || urlParam('selectedCarrierCode') || '',
            data.logisticGroup[0].list
          ); // expressData 结构如{ innerFloor:{} , outerFloor:{} , skuModel:{} }1

          const {
            method,
            methodCode,
            saveCost,
            dollarPriceNumber,
            originalCostNumber,
            activityMaterials,
            deliveryDateText,
            hasAliLogistics,
            leadTime,
            ftDetailId,
            dispatchLocation,
            country,
            freightGuaranteeResultDTO,
          } = expressData.innerFloor.data;

          const expressInfo = {
            method: method, //物流线路名称，"Standard"
            methodCode: methodCode, //物流线路freightCarrierCode，"SEMI_MANAGED_STANDARD@@STANDARD"，需要传给下单页
            saveCost: saveCost, //优惠了的多少钱
            cost: dollarPriceNumber, //dollarPriceNumber美元价格
            originalCost: originalCostNumber, //原价，有价格优惠时才会透出
            activityMaterials: activityMaterials, //sku面板内的free shipping氛围条数据
            ftDetailId: ftDetailId, //需要传给下单页
            deliveryDateText: deliveryDateText, //"Estimated delivery by Jun 28-Jul 4"
            leadTime: leadTime,
            hasAliLogistics: hasAliLogistics,
            dispatchLocation: dispatchLocation,
            country: country,
            freightGuaranteeResultDTO: freightGuaranteeResultDTO,
            showShippingModule: data.showShippingModule,
          };

          return {
            express: expressInfo, // expressInfo 结构如 ExpressInfo类型 所示
            isEmpty: false,
            isCall: true,
            logisticPrice: quantity === 0 ? 0 : expressInfo.cost, //cost是商详后端传的dollarPriceNumber是美元价格，用来算价格
            selected: state[type].selected,
            // 请求参数
            arg: params,
          };
        })(),
      }));
    }
    // 无物流
    else {
      set({
        [type]: {
          ...defaultObj,
          isEmpty: true,
          isCall: true,
          // 请求参数
          arg: params,
        },
      });
    }
  },

  // 更新物流发货地数据
  setDispatchById: dispatchId => {
    set({
      dispatchId,
    });
  },

  upExpress: payload => {
    const { type, express, selected, quantity = 0 } = payload;
    if (express) {
      set(state => ({
        ...state,
        [type]: {
          ...state[type],
          express,
          selected,
          logisticPrice: quantity === 0 ? 0 : express.cost,
          isCall: Object.keys(express).length !== 0,
          isEmpty: Object.keys(express).length === 0,
        },
      }));
    }
  },
}));

export default ExpressStore;
